import { useEffect, useRef } from 'react'
import { useQueryParam, StringParam } from 'use-query-params'
import { useDebouncedState } from '@retailer-platform/shared-components/src/hooks/useDebouncedState.hooks'
import {
  type AccountStatusEnum,
  useSearchEnterpriseAccountsQuery,
} from 'domains/account-management/api'
import { usePaginationQueryParams } from 'utils/query-params/usePaginationQueryParams.hooks'
interface UseAdminAccountsOptions {
  partnerId: string
}

export const useAdminAccounts = ({ partnerId }: UseAdminAccountsOptions) => {
  const [statusQueryParam, setStatusQueryParam] = useQueryParam('status', StringParam)
  const [roleQueryParam, setRoleQueryParam] = useQueryParam('role', StringParam)
  const [emailQueryParam, setEmailQueryParam] = useQueryParam('email', StringParam)
  const [email, debouncedEmail, setEmail] = useDebouncedState(emailQueryParam, 500)

  const [pageQueryParams, setPage] = usePaginationQueryParams()

  const afterFirstEffectCall = useRef(false)

  // Reset page to 1 on any filter change
  useEffect(() => {
    // Ignore the first call as we'll be using the incoming query params
    if (!afterFirstEffectCall.current) {
      afterFirstEffectCall.current = true
      return
    }

    setPage(1)
  }, [statusQueryParam, roleQueryParam, emailQueryParam, setPage])

  const { itemsPerPage, page } = pageQueryParams

  const { data, loading, error, refetch } = useSearchEnterpriseAccountsQuery({
    variables: {
      email: debouncedEmail,
      roleId: roleQueryParam,
      status: statusQueryParam as AccountStatusEnum,
      partnerId,
      limit: itemsPerPage,
      offset: (page - 1) * itemsPerPage,
    },
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    setEmailQueryParam(debouncedEmail)
  }, [debouncedEmail, setEmailQueryParam])

  return {
    setStatus: setStatusQueryParam,
    status: statusQueryParam as AccountStatusEnum,
    setRole: setRoleQueryParam,
    role: roleQueryParam,
    email,
    setEmail,
    accounts: data?.enterpriseAccountSearch?.edges.map(edge => edge.node),
    totalPages: Math.ceil(data?.enterpriseAccountSearch?.meta?.totalCount / itemsPerPage),
    loading,
    error,
    refetch,
  }
}
